import * as Utilities from "./utilities";
import { fetchAuthSession } from "aws-amplify/auth";
import "../auth/amplifyConfig";
import { showToast } from "../ui/Components/Toast";

export { searchAll, searchCompanies, searchOfficers, searchAdvanced };
export { getCompany, getCompanyOfficers, getResource, getResourcePage };
export { getOwners, getSubsidiaries, getLand, getFinancials, getShareholders };
export { getGazetteNotices, getIcoRegistration };
export { getPdfMetaResource, downloadCompanyReport };
export { listPortfolios, createPortfolio, renamePortfolio, setPortfolioAlerts, deletePortfolio };
export { listPortfolioItems, addPortfolioItem, removePortfolioItem };
export { listSavedSearches, createSavedSearch, renameSavedSearch, setSavedSearchAlerts, deleteSavedSearch, getSavedSearchMatches };
export { getSavedSearchesForCompany, setSavedSearchLastViewed };
export { savedSearchFiltersToAdvancedSearchFilters };
export { createCheckoutSession, createPortalSession, getBillingStatus, rotateApiKey, getPrices };
//import { RNFetchBlob } from 'react-native-fetch-blob';

// ------------------------------------------------------
// Exported Functions
// ------------------------------------------------------
// API: https://developer.companieshouse.gov.uk/api/docs/search/search.html
// Response: https://developer.companieshouse.gov.uk/api/docs/search-overview/Search-resource.html
async function searchAll(queryString: string) {
  let uri = pathToUri(["search"]);
  return await get(uri, queryString);
}

// ------------------------------------------------------
// API: https://developer.companieshouse.gov.uk/api/docs/search/companies/companysearch.html
// Response : https://developer.companieshouse.gov.uk/api/docs/search-overview/CompanySearch-resource.html
async function searchCompanies(queryString: string) {
  let uri = pathToUri(["search", "companies"]);
  return await get(uri, queryString);
}

// ------------------------------------------------------
// API: https://developer.companieshouse.gov.uk/api/docs/search/officers/officersearch.html
// Response: https://developer.companieshouse.gov.uk/api/docs/search-overview/OfficerSearch-resource.html
async function searchOfficers(queryString: string) {
  let uri = pathToUri(["search", "officers"]);
  return await get(uri, queryString);
}

// ------------------------------------------------------
// API: https://developer.companieshouse.gov.uk/api/docs/company/company_number/readCompanyProfile.html
// Response: https://developer.companieshouse.gov.uk/api/docs/company/company_number/companyProfile-resource.html
async function getCompany(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// API: https://developer.companieshouse.gov.uk/api/docs/company/company_number/officers/officers.html
// Response: https://developer.companieshouse.gov.uk/api/docs/company/company_number/officers/officerList-resource.html
async function getCompanyOfficers(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "officers"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// API: https://developer.companieshouse.gov.uk/api/docs/officers/officer_id/appointments/appointmentList.html
// Response: https://developer.companieshouse.gov.uk/api/docs/officers/officer_id/appointments/appointmentList-resource.html
async function getResource(officerUri: string) {
  return await get(officerUri, undefined);
}

// ------------------------------------------------------
// Same as getResource, but for paginating through a list resource whose
// total_count exceeds what a single Companies House call returns (their
// items_per_page cap is 100 regardless of what's requested) - start_index is
// an offset (0, 100, 200, ...), not a page number.
async function getResourcePage(resourceUri: string, startIndex: number) {
  const separator = resourceUri.includes("?") ? "&" : "?";
  const uri = `${resourceUri}${separator}items_per_page=100&start_index=${startIndex}`;
  return await httpGetJSON(API_ENDPOINT + uri, await buildHeaders());
}

// ------------------------------------------------------
// PRO: corporate entities that control this company (its parents), derived
// by DeltaGain from the PSC register rather than a plain Companies House field.
async function getOwners(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "owners"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// PRO: companies this one controls (its subsidiaries), the reverse of getOwners.
async function getSubsidiaries(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "subsidiaries"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// PRO: land/property titles owned by this company, from HM Land Registry's
// UK Companies that own property dataset.
async function getLand(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "land"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// PRO: lazily-computed key financial metrics per accounting period, parsed by
// DeltaGain from Companies House's raw XBRL/iXBRL accounts filings.
async function getFinancials(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "financials"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// PRO: shareholder names/shares/percentages, parsed by DeltaGain from the
// most recent Companies House confirmation statement that actually includes
// a full shareholder breakdown (not every confirmation statement has one).
async function getShareholders(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "shareholders"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// PRO: insolvency/petition notices from The Gazette (winding-up petitions,
// liquidator appointments, etc.) - often precede a Companies House filing
// by weeks, so this is a separate, earlier signal from Filing History.
async function getGazetteNotices(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "gazette-notices"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
// PRO: whether a company is registered as a data controller with the ICO
// (UK data protection regulator), and its fee tier - matched by DeltaGain
// from the ICO's Register of Fee Payers onto Companies House data purely by
// name. registered: false is the normal response for most companies, not an
// error - see lambda_ico_lookup.py.
async function getIcoRegistration(companyNumberAsString: string) {
  let uri = pathToUri(["company", companyNumberAsString, "ico-registration"]);
  return await get(uri, undefined);
}

// ------------------------------------------------------
async function getPdfMetaResource(pathUrl: any) {
  return await httpGetJSON(API_ENDPOINT + pathUrl, await buildHeaders());
}

// ------------------------------------------------------
// PRO: filters companies by SIC code, status, location, and incorporation
// date range via Athena, rather than a plain name search - each filter param
// is omitted from the query string entirely when not provided. "location"
// matches postcode by prefix and town/county by substring on the backend.
export type AdvancedSearchFilters = {
  name?: string;
  sicCode?: string;
  status?: string;
  location?: string;
  incorporatedAfter?: string;
  incorporatedBefore?: string;
};

async function searchAdvanced(filters: AdvancedSearchFilters) {
  const params = new URLSearchParams();
  if (filters.name) params.set("name", filters.name);
  if (filters.sicCode) params.set("sic_code", filters.sicCode);
  if (filters.status) params.set("status", filters.status);
  if (filters.location) params.set("location", filters.location);
  if (filters.incorporatedAfter) params.set("incorporated_after", filters.incorporatedAfter);
  if (filters.incorporatedBefore) params.set("incorporated_before", filters.incorporatedBefore);

  const fullUri = `${API_ENDPOINT}/search/advanced?${params.toString()}`;
  return await httpGetJSON(fullUri, await buildHeaders());
}

// ------------------------------------------------------
// Enterprise: renders a branded PDF report server-side. A plain <a href>
// can't attach the Authorization header, so this fetches the PDF as a blob
// and triggers the download client-side instead - the explicit Accept header
// also happens to be what makes API Gateway correctly base64-decode the
// binary response (its "*/*" binaryMediaTypes match otherwise depends on
// what a given browser's default navigation Accept header happens to be).
async function downloadCompanyReport(companyNumberAsString: string) {
  const uri = pathToUri(["company", companyNumberAsString, "report"]);
  const headers = { ...(await buildHeaders()), Accept: "application/pdf" };
  const response = await fetch(API_ENDPOINT + uri, { headers });
  if (!response.ok) {
    throw new Error(`Report download failed: ${response.status}`);
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  // The backend names the file "{number}-{company name}-{date}.pdf" via
  // Content-Disposition; fall back to a plain number-only name if that
  // header is ever missing rather than duplicating its naming logic here.
  const disposition = response.headers.get("Content-Disposition");
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  link.download = filenameMatch ? filenameMatch[1] : `${companyNumberAsString}-report.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// ------------------------------------------------------
// Portfolio/favourites - every user has an implicit "default" portfolio with
// no metadata row; only enterprise users can create additional named ones.
async function listPortfolios() {
  return await httpGetJSON(`${API_ENDPOINT}/portfolios`, await buildHeaders());
}

async function createPortfolio(name: string, alertsEnabled?: boolean) {
  const body: { name: string; alerts_enabled?: boolean } = { name };
  if (alertsEnabled !== undefined) {
    body.alerts_enabled = alertsEnabled;
  }
  return await httpPostJSON(`${API_ENDPOINT}/portfolios`, body, await buildHeaders());
}

async function renamePortfolio(portfolioId: string, name: string) {
  return await httpPatchJSON(`${API_ENDPOINT}/portfolios/${portfolioId}`, { name }, await buildHeaders());
}

async function setPortfolioAlerts(portfolioId: string, alertsEnabled: boolean) {
  return await httpPatchJSON(
    `${API_ENDPOINT}/portfolios/${portfolioId}`,
    { alerts_enabled: alertsEnabled },
    await buildHeaders()
  );
}

async function deletePortfolio(portfolioId: string) {
  return await httpDeleteJSON(`${API_ENDPOINT}/portfolios/${portfolioId}`, await buildHeaders());
}

async function listPortfolioItems(portfolioId: string) {
  return await httpGetJSON(`${API_ENDPOINT}/portfolios/${portfolioId}/items`, await buildHeaders());
}

async function addPortfolioItem(portfolioId: string, companyNumber: string, companyName: string) {
  return await httpPostJSON(
    `${API_ENDPOINT}/portfolios/${portfolioId}/items`,
    { company_number: companyNumber, company_name: companyName },
    await buildHeaders()
  );
}

async function removePortfolioItem(portfolioId: string, companyNumber: string) {
  return await httpDeleteJSON(`${API_ENDPOINT}/portfolios/${portfolioId}/items/${companyNumber}`, await buildHeaders());
}

// ------------------------------------------------------
// Enterprise: saved search alerts - a saved search stores the CH-facing
// snake_case filter keys (same wire shape searchAdvanced sends), not the
// frontend's camelCase AdvancedSearchFilters field names. incorporatedAfter/
// incorporatedBefore are deliberately never stored here - the backend
// recomputes a fresh date window against "now" every time it re-runs a saved
// search, so a stored absolute date would go stale immediately.
export type SavedSearchFilters = {
  name?: string;
  sic_code?: string;
  status?: string;
  location?: string;
};

// Display-only snapshot of whatever date filter was active in the UI at the
// moment a search was saved as a Dynamic Portfolio - NEVER used for ongoing
// matching (see lambda_monitor_portfolios.py's _ch_advanced_search and
// lambda_saved_searches.py's _ch_seed_search, both of which continue to only
// read sic_code/status/location/name). Stored purely so
// DynamicPortfoliosPanel.tsx can show what date range the user actually
// searched with at save time, since the daily job intentionally re-derives
// its own "since last check" window rather than reusing a fixed one.
export type SavedSearchDisplayFilters = {
  incorporated_after?: string;
  incorporated_before?: string;
};

// Inverse of SearchResultPage.tsx's toSavedSearchFilters - used by
// DynamicPortfoliosPanel's "Run this search again" action, which needs to
// hand a saved search's stored snake_case filters back to SearchResultPage as
// the camelCase shape it works with internally. displayFilters is optional
// purely for display-time convenience (pre-filling the date fields the user
// had active when they saved this search) - it never affects what gets
// persisted if the user saves again from this re-run.
function savedSearchFiltersToAdvancedSearchFilters(
  filters: SavedSearchFilters,
  displayFilters?: SavedSearchDisplayFilters
): AdvancedSearchFilters {
  return {
    name: filters.name,
    sicCode: filters.sic_code,
    status: filters.status,
    location: filters.location,
    incorporatedAfter: displayFilters?.incorporated_after,
    incorporatedBefore: displayFilters?.incorporated_before,
  };
}

async function listSavedSearches() {
  return await httpGetJSON(`${API_ENDPOINT}/saved-searches`, await buildHeaders());
}

async function createSavedSearch(
  name: string,
  filters: SavedSearchFilters,
  displayFilters?: SavedSearchDisplayFilters,
  alertsEnabled?: boolean
) {
  // display_incorporated_after/display_incorporated_before ride inside the
  // same "filters" body object as sic_code/status/location/name (simplest
  // wire shape - one object), but the backend picks them out into their own
  // separate display_filters attribute rather than folding them into the
  // matching filter set - see lambda_saved_searches.py's _create_saved_search.
  const body: {
    name: string;
    filters: SavedSearchFilters & { display_incorporated_after?: string; display_incorporated_before?: string };
    alerts_enabled?: boolean;
  } = { name, filters: { ...filters } };
  if (displayFilters?.incorporated_after) body.filters.display_incorporated_after = displayFilters.incorporated_after;
  if (displayFilters?.incorporated_before) body.filters.display_incorporated_before = displayFilters.incorporated_before;
  if (alertsEnabled !== undefined) {
    body.alerts_enabled = alertsEnabled;
  }
  return await httpPostJSON(`${API_ENDPOINT}/saved-searches`, body, await buildHeaders());
}

async function renameSavedSearch(searchId: string, name: string) {
  return await httpPatchJSON(`${API_ENDPOINT}/saved-searches/${searchId}`, { name }, await buildHeaders());
}

async function setSavedSearchAlerts(searchId: string, alertsEnabled: boolean) {
  return await httpPatchJSON(
    `${API_ENDPOINT}/saved-searches/${searchId}`,
    { alerts_enabled: alertsEnabled },
    await buildHeaders()
  );
}

async function deleteSavedSearch(searchId: string) {
  return await httpDeleteJSON(`${API_ENDPOINT}/saved-searches/${searchId}`, await buildHeaders());
}

// Every company matched by this Dynamic Portfolio's daily alert run, most
// recent first (server-sorted by matched_at) - persisted server-side now
// rather than only ever appearing once in an email digest. Also carries
// back last_viewed_at (absent/null if this search has never been viewed) so
// callers can diff each match's matched_at against it to work out what's
// new since the last visit - see DynamicPortfoliosPanel.tsx.
async function getSavedSearchMatches(searchId: string) {
  return await httpGetJSON(`${API_ENDPOINT}/saved-searches/${searchId}/matches`, await buildHeaders());
}

// Which Dynamic Portfolios (saved searches) the given company currently
// appears in, scoped to the signed-in user's own searches only - powers the
// dynamic-portfolio membership pills on the company details page
// (CompanyActions.tsx), alongside the existing static-portfolio pills.
async function getSavedSearchesForCompany(companyNumber: string) {
  return await httpGetJSON(`${API_ENDPOINT}/saved-searches/for-company/${companyNumber}`, await buildHeaders());
}

// Records that the signed-in user has just viewed this Dynamic Portfolio's
// match list, so a subsequent visit's "New" badges only cover matches that
// arrived after this moment - see DynamicPortfoliosPanel.tsx for the
// capture-before-mark-as-viewed ordering this depends on.
async function setSavedSearchLastViewed(searchId: string, lastViewedAt: string) {
  return await httpPatchJSON(
    `${API_ENDPOINT}/saved-searches/${searchId}`,
    { last_viewed_at: lastViewedAt },
    await buildHeaders()
  );
}

// ------------------------------------------------------
// Billing/Stripe - product_key (NOT "product") is the field the backend
// expects; the 6 keys map to the 4 web Checkout products plus the 2 API-only
// products, though only pro_monthly/api_enterprise have real Stripe Price IDs
// wired up server-side today - the rest 400 until that config lands.
export type ProductKey = "pro_monthly" | "pro_yearly" | "enterprise_monthly" | "enterprise_yearly" | "api_pro" | "api_enterprise";

export type BillingStatus = {
  web_plan: "free" | "pro" | "enterprise";
  web_status: "active" | "past_due" | "canceled" | null;
  web_billing_cycle: "monthly" | "yearly" | null;
  api_plan: "none" | "api_pro" | "api_enterprise";
  api_status: "active" | "disabled" | "canceled" | null;
  api_key_last4: string | null;
};

export type PriceInfo = { amount: number; currency: string; interval: "month" | "year" | null };
export type Prices = Record<ProductKey, PriceInfo>;

// Public, no auth needed - reads live from Stripe (via a read-only key) so
// nothing is hardcoded here; prices displayed always match what Checkout
// will actually charge once a product's Price ID is wired up.
async function getPrices(): Promise<Prices | undefined> {
  return await httpGetJSON(`${API_ENDPOINT}/billing/prices`, await buildHeaders());
}

async function createCheckoutSession(productKey: ProductKey) {
  return await httpPostJSON(`${API_ENDPOINT}/billing/checkout`, { product_key: productKey }, await buildHeaders());
}

async function createPortalSession() {
  return await httpPostJSON(`${API_ENDPOINT}/billing/portal`, {}, await buildHeaders());
}

async function getBillingStatus(): Promise<BillingStatus | undefined> {
  return await httpGetJSON(`${API_ENDPOINT}/billing/status`, await buildHeaders());
}

async function rotateApiKey() {
  return await httpPostJSON(`${API_ENDPOINT}/billing/api-key/rotate`, {}, await buildHeaders());
}

// ------------------------------------------------------
// async function getPdf(fullUrl) {

//   Utilities.log('http pdf get: ' + fullUrl);

//     let response = await fetch(fullUrl, {headers: PREMIUM_HEADERS})
//     .then(async (response) => {
//       if (response.status >= 200 && response.status < 300) {
//         let url = await response.url;
//         Utilities.log('http ok. pdf url is: ');
//         return  url;
//       } else {
//         console.error('http error: ' + response.status);
//         {error: response.status}
//       }
//     })
//     .then((url) => {
//       return url;
//     })
//     .catch((error) => {
//       console.error('http error: ' + error);
//     });

//     return response;
//   }

// // ------------------------------------------------------
//   function getPdfHeaders() {
//     return PREMIUM_HEADERS;
//   }

// ------------------------------------------------------
// Local Functions
// ------------------------------------------------------
async function get(uri: any, querySearchString: any) {
  let fullUri = API_ENDPOINT + uri;
  if (querySearchString != undefined) {
    fullUri = fullUri + "?q=" + querySearchString;
  } else {
    fullUri = fullUri + "?items_per_page=100";
  }

  Utilities.log("http get: " + fullUri);

  return await httpGetJSON(fullUri, await buildHeaders());
}

// ------------------------------------------------------
// Attaches the signed-in user's ID token alongside the existing API key on
// every request, not just PRO ones - most routes don't need it, but sending
// it uniformly means Slack logging can show who made any request, not just
// the ones actually gated behind it. fetchAuthSession() resolves to a
// tokenless session (not a rejection) when signed out, so this is safe to
// call unconditionally.
async function buildHeaders() {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    return idToken ? { ...HEADERS, Authorization: `Bearer ${idToken}` } : HEADERS;
  } catch {
    return HEADERS;
  }
}

// ------------------------------------------------------
// A human-readable explanation for a failed request, shown as a toast so a
// real visitor (not just a developer in devtools) understands what happened
// instead of just seeing a section come back empty. 429 gets its own
// friendlier wording since it's the one case that's neither an outage nor a
// mistake on the visitor's part - they've just hit the site's shared rate
// limit and trying again shortly will work.
function friendlyErrorMessage(status: number): string {
  if (status === 429) {
    return "You're browsing a little too quickly - please wait a moment and try again.";
  }
  if (status >= 500) {
    return "Something went wrong on our end. Please try again in a moment.";
  }
  return "Something went wrong with that request. Please try again.";
}

// ------------------------------------------------------
async function httpGetJSON(fullUri: any, headers: any = HEADERS) {
  console.log("fullUri: " + fullUri);
  let response = await fetch(fullUri, { headers })
    .then(async (response) => {
      if (response.status >= 200 && response.status < 300) {
        Utilities.log("http ok");
        let responseJson = await response.json();
        return responseJson;
      } else {
        console.error("http error: " + response.status);
        showToast(friendlyErrorMessage(response.status));
      }
    })
    .then((responseJson) => {
      return responseJson;
    })
    .catch((error) => {
      console.error("http error: " + error);
    });

  return response;
}

// ------------------------------------------------------
async function httpPostJSON(fullUri: any, body: any, headers: any = HEADERS) {
  const response = await fetch(fullUri, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    showToast(friendlyErrorMessage(response.status));
  }
  return await response.json().catch(() => undefined);
}

// ------------------------------------------------------
async function httpDeleteJSON(fullUri: any, headers: any = HEADERS) {
  const response = await fetch(fullUri, { method: "DELETE", headers });
  if (!response.ok) {
    showToast(friendlyErrorMessage(response.status));
  }
  return await response.json().catch(() => undefined);
}

// ------------------------------------------------------
async function httpPatchJSON(fullUri: any, body: any, headers: any = HEADERS) {
  const response = await fetch(fullUri, {
    method: "PATCH",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    showToast(friendlyErrorMessage(response.status));
  }
  return await response.json().catch(() => undefined);
}

// ------------------------------------------------------
function pathToUri(path: any) {
  return "/" + path.join("/");
}

// ------------------------------------------------------
// Macros
// ------------------------------------------------------
// In development, fetches use a relative path so webpack-dev-server's "proxy"
// setting (package.json) forwards them server-side - the API's CORS policy
// only allows the production origin, so an absolute URL here would just get
// blocked by the browser when running locally.
export const API_ENDPOINT =
  process.env.NODE_ENV === "development" ? "" : "https://api.directory.deltagain.co.uk";

export const HEADERS = {
  "x-api-key": "eicBusDN9o1PlhFaMVpK3XSxByZegRfpRxZbDrj0",
};
const PREMIUM_HEADERS = HEADERS;
